import type { CashflowGroup, Currency } from '../lib/api/types'

export type GroupBy = 'category' | 'project'
export type PeriodGrain = 'month' | 'quarter' | 'year'
export type CurrencyMode = 'base' | 'native'
export type DrillCell = { group: CashflowGroup; periodKey: string }

export function defaultRange(): { from: string; to: string } {
  const today = new Date()
  const to = today.toISOString().slice(0, 10)
  const fromDate = new Date(today)
  fromDate.setMonth(fromDate.getMonth() - 5)
  fromDate.setDate(1)
  const from = fromDate.toISOString().slice(0, 10)
  return { from, to }
}

export function periodToDateRange(
  periodKey: string,
  grain: PeriodGrain,
): { from: string; to: string } {
  if (grain === 'month') {
    const [y, m] = periodKey.split('-').map(Number)
    const from = `${y}-${String(m).padStart(2, '0')}-01`
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
    const to = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`
    return { from, to }
  }
  if (grain === 'year') {
    return { from: `${periodKey}-01-01`, to: `${periodKey}-12-31` }
  }
  const [yStr, qStr] = periodKey.split('-Q')
  const y = Number(yStr)
  const q = Number(qStr)
  const fromMonth = (q - 1) * 3 + 1
  const toMonth = q * 3
  const from = `${y}-${String(fromMonth).padStart(2, '0')}-01`
  const lastDay = new Date(Date.UTC(y, toMonth, 0)).getUTCDate()
  const to = `${y}-${String(toMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  return { from, to }
}

// Render a cashflow cell. Signs are encoded as colors instead of glyphs
// (positive → success-fg, negative → danger-fg) — matches the green/red used
// for income/expense in transactions and removes one visual layer to scan.
// Kopecks are dropped — pivot rows aggregate by category so cents add noise
// without recall value.
function groupDigits(whole: number): string {
  return Math.abs(whole)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

function colorFor(n: number): string {
  if (n > 0) return 'var(--success-fg)'
  if (n < 0) return 'var(--danger-fg)'
  return 'var(--fg-4)'
}

export function CashflowAmount({
  v,
  ccy = 'UAH',
  bold = false,
}: {
  v: number | Record<string, number> | undefined
  ccy?: Currency
  bold?: boolean
}) {
  if (v === undefined) return <span style={{ color: 'var(--fg-4)' }}>—</span>
  if (typeof v === 'number') {
    if (v === 0) return <span style={{ color: 'var(--fg-4)' }}>—</span>
    return (
      <span style={{ color: colorFor(v), fontWeight: bold ? 600 : undefined }}>
        {groupDigits(Math.round(v))} <span style={{ opacity: 0.6 }}>{ccy}</span>
      </span>
    )
  }
  const parts = Object.entries(v).filter(([, val]) => val !== 0)
  if (parts.length === 0) return <span style={{ color: 'var(--fg-4)' }}>—</span>
  return (
    <span style={{ fontWeight: bold ? 600 : undefined }}>
      {parts.map(([c, val], i) => (
        <span key={c}>
          {i > 0 && <span style={{ color: 'var(--fg-4)' }}> · </span>}
          <span style={{ color: colorFor(val) }}>
            {groupDigits(Math.round(val))} <span style={{ opacity: 0.6 }}>{c}</span>
          </span>
        </span>
      ))}
    </span>
  )
}
